import type { CancelablePromise } from "../client"
import { OpenAPI } from "../client"
import { request as __request } from "../client/core/request"

export type InventoryCountAdjustmentSpikeDTO = {
  product_id: string
  product_sku: string
  product_name: string
  warehouse_id: string
  warehouse_code: string
  warehouse_name: string
  location_code: string
  total_adjustment_qty: number
  baseline_avg_qty: number
  spike_ratio: number
  movements_count: number
}

export type InventoryCountFrequentRecountDTO = {
  product_id: string
  product_sku: string
  product_name: string
  location_code: string
  recount_count: number
  task_count: number
}

export type InventoryCountAnomalyAlertsDTO = {
  warehouse_id?: string
  from: string
  to: string
  generated_at: string
  adjustment_spikes: InventoryCountAdjustmentSpikeDTO[]
  frequent_recounts: InventoryCountFrequentRecountDTO[]
}

export type InventoryCountKPIDTO = {
  warehouse_id?: string
  from: string
  to: string
  total_tasks: number
  pending_review_tasks: number
  recounted_tasks: number
  recount_rate_pct: number
  counted_lines: number
  accurate_lines: number
  accuracy_pct: number
  adjustment_value_total: number
}

export type InventoryCountTaskDrilldownItemDTO = {
  task_id: string
  task_no: string
  status: string
  warehouse_id: string
  product_id: string
  location_code: string
  count_round: number
  counted_qty?: number
  variance_qty?: number
  variance_value?: number
  submitted_at?: string
  approved_at?: string
  posted_at?: string
  task_created_at: string
}

export type InventoryCountTaskDrilldownResponseDTO = {
  items: InventoryCountTaskDrilldownItemDTO[]
  total: number
  page: number
  page_size: number
}

export type InventoryCountLocationLockItemDTO = {
  id: string
  task_id: string
  task_no: string
  task_status: string
  warehouse_id: string
  location_code: string
  locked_by_id: string
  locked_at: string
}

export type InventoryCountLocationLockListResponseDTO = {
  items: InventoryCountLocationLockItemDTO[]
  total: number
  page: number
  page_size: number
}

export type InventoryCountActiveWarehousesDTO = {
  warehouse_ids: string[]
}

export type InventoryCountBlockingDraftDocumentDTO = {
  document_type: string
  document_id: string
  document_no: string
  status: string
  created_at: string
}

export type InventoryCountBlockingDraftDocumentsDTO = {
  warehouse_id: string
  items: InventoryCountBlockingDraftDocumentDTO[]
  total: number
}

export type InventoryCountPlanDTO = {
  id: string
  warehouse_id: string
  name: string
  strategy: string
  frequency: string
  status: string
  zone_filter?: string
  location_filter?: string
  next_run_at?: string
  last_run_at?: string
  active_task?: {
    id: string
    task_no: string
    status: string
    created_at: string
  }
  created_at: string
  updated_at: string
}

export type InventoryCountPlanListResponseDTO = {
  items: InventoryCountPlanDTO[]
  total: number
  page: number
  page_size: number
}

export type CreateInventoryCountPlanDTO = {
  warehouse_id: string
  name: string
  strategy: string
  frequency: string
  status?: string
  zone_filter?: string
  location_filter?: string
  next_run_at?: string
}

export type UpdateInventoryCountPlanDTO = {
  warehouse_id?: string
  name?: string
  strategy?: string
  frequency?: string
  status?: string
  zone_filter?: string
  location_filter?: string
  next_run_at?: string
}

export type GenerateTasksFromPlanDTO = {
  limit?: number
  scope_type?: string
  priority?: number
  due_hours?: number
}

export type GenerateTasksResultDTO = {
  task_id: string
  task_no: string
  lines_count: number
}

export type RunDuePlansResultItemDTO = {
  plan_id: string
  task_id?: string
  task_no?: string
  status: string
  message?: string
}

export type RunDuePlansResultDTO = {
  processed_at: string
  requested_by: string
  limit: number
  processed: number
  generated: number
  failed: number
  items: RunDuePlansResultItemDTO[]
}

export type InventoryCountTaskDTO = {
  id: string
  plan_id?: string
  warehouse_id: string
  task_no: string
  scope_type: string
  scope_limit?: number
  scope_zone_filter?: string
  scope_location_filter?: string
  status: string
  priority: number
  due_at?: string
  started_at?: string
  submitted_at?: string
  approved_at?: string
  posted_at?: string
  assigned_to_user_id?: string
  created_at: string
  updated_at: string
}

export type InventoryCountTaskListResponseDTO = {
  items: InventoryCountTaskDTO[]
  total: number
  page: number
  page_size: number
}

export type InventoryCountTaskLineDTO = {
  id: string
  product_id: string
  product_sku: string
  product_name: string
  product_ean?: string
  location_code: string
  warehouse_lot_id?: string
  lot_no?: string
  serial_no?: string
  system_qty_snapshot: number
  counted_qty?: number
  variance_qty?: number
  variance_value?: number
  count_round: number
  count_state: string
  counted_by_user_id?: string
  counted_at?: string
}

export type InventoryCountApprovalDTO = {
  id: string
  decision: string
  reason?: string
  approved_by_user_id: string
  approved_at: string
}

export type InventoryCountTaskDetailDTO = {
  id: string
  plan_id?: string
  warehouse_id: string
  task_no: string
  scope_type: string
  scope_limit?: number
  scope_zone_filter?: string
  scope_location_filter?: string
  status: string
  priority: number
  due_at?: string
  started_at?: string
  submitted_at?: string
  approved_at?: string
  posted_at?: string
  assigned_to_user_id?: string
  who_created_by_id: string
  who_updated_by_id: string
  created_at: string
  updated_at: string
  lines: InventoryCountTaskLineDTO[]
  approvals: InventoryCountApprovalDTO[]
}

export const InventoryCountApi = {
  getPlans: (params?: {
    warehouse_id?: string
    status?: string
    page?: number
    page_size?: number
  }): CancelablePromise<InventoryCountPlanListResponseDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/inventory-count/plans",
      query: {
        warehouse_id: params?.warehouse_id || undefined,
        status: params?.status || undefined,
        page: params?.page || undefined,
        page_size: params?.page_size || undefined,
      },
    })
  },
  createPlan: (
    payload: CreateInventoryCountPlanDTO,
  ): CancelablePromise<InventoryCountPlanDTO> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/inventory-count/plans",
      body: payload,
      mediaType: "application/json",
    })
  },
  updatePlan: (
    planId: string,
    payload: UpdateInventoryCountPlanDTO,
  ): CancelablePromise<InventoryCountPlanDTO> => {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/inventory-count/plans/${planId}`,
      body: payload,
      mediaType: "application/json",
    })
  },
  deletePlan: (planId: string): CancelablePromise<void> => {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/inventory-count/plans/${planId}`,
    })
  },
  generateTasksFromPlan: (
    planId: string,
    payload: GenerateTasksFromPlanDTO = {},
  ): CancelablePromise<GenerateTasksResultDTO> => {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/inventory-count/plans/${planId}/generate-tasks`,
      body: payload,
      mediaType: "application/json",
    })
  },
  runDuePlans: (limit = 25): CancelablePromise<RunDuePlansResultDTO> => {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/inventory-count/plans/run-due",
      query: {
        limit,
      },
    })
  },
  getTasks: (params?: {
    warehouse_id?: string
    status?: string
    assigned_to?: string
    my_tasks?: boolean
    location_code?: string
    has_variance?: boolean
    page?: number
    page_size?: number
  }): CancelablePromise<InventoryCountTaskListResponseDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/inventory-count/tasks",
      query: {
        warehouse_id: params?.warehouse_id || undefined,
        status: params?.status || undefined,
        assigned_to: params?.assigned_to || undefined,
        my_tasks: params?.my_tasks ? "true" : undefined,
        location_code: params?.location_code || undefined,
        has_variance: params?.has_variance ? "true" : undefined,
        page: params?.page || undefined,
        page_size: params?.page_size || undefined,
      },
    })
  },
  startTask: (taskId: string): CancelablePromise<{ status: string }> => {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/inventory-count/tasks/${taskId}/start`,
    })
  },
  assignTask: (
    taskId: string,
    assignedToUserId?: string,
  ): CancelablePromise<{ status: string }> => {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/inventory-count/tasks/${taskId}/assign`,
      body: {
        assigned_to_user_id: assignedToUserId || undefined,
      },
      mediaType: "application/json",
    })
  },
  getTaskById: (
    taskId: string,
  ): CancelablePromise<InventoryCountTaskDetailDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/inventory-count/tasks/${taskId}`,
    })
  },
  countTaskLines: (
    taskId: string,
    lines: Array<{
      line_id: string
      counted_qty: number
      count_state?: string
    }>,
  ): CancelablePromise<{ status: string }> => {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/inventory-count/tasks/${taskId}/count-lines`,
      body: { lines },
      mediaType: "application/json",
    })
  },
  submitTask: (taskId: string): CancelablePromise<{ status: string }> => {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/inventory-count/tasks/${taskId}/submit`,
    })
  },
  approveTask: (
    taskId: string,
    payload: { decision: "approve" | "reject"; reason?: string },
  ): CancelablePromise<{ status: string }> => {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/inventory-count/tasks/${taskId}/approve`,
      body: payload,
      mediaType: "application/json",
    })
  },
  recountTask: (
    taskId: string,
    payload: { reason?: string } = {},
  ): CancelablePromise<{ status: string }> => {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/inventory-count/tasks/${taskId}/recount`,
      body: payload,
      mediaType: "application/json",
    })
  },
  postTask: (taskId: string): CancelablePromise<{ status: string }> => {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/inventory-count/tasks/${taskId}/post`,
    })
  },
  getKPI: (params?: {
    warehouse_id?: string
    from?: string
    to?: string
  }): CancelablePromise<InventoryCountKPIDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/inventory-count/kpi",
      query: {
        warehouse_id: params?.warehouse_id || undefined,
        from: params?.from || undefined,
        to: params?.to || undefined,
      },
    })
  },
  getAnomalyAlerts: (params?: {
    warehouse_id?: string
    from?: string
    to?: string
  }): CancelablePromise<InventoryCountAnomalyAlertsDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/inventory-count/alerts/anomalies",
      query: {
        warehouse_id: params?.warehouse_id || undefined,
        from: params?.from || undefined,
        to: params?.to || undefined,
      },
    })
  },
  getTaskDrilldown: (params: {
    product_id: string
    location_code: string
    warehouse_id?: string
    from?: string
    to?: string
    page?: number
    page_size?: number
    sort_by?: "created_at" | "status"
    sort_dir?: "asc" | "desc"
  }): CancelablePromise<InventoryCountTaskDrilldownResponseDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/inventory-count/tasks/drilldown",
      query: {
        product_id: params.product_id,
        location_code: params.location_code,
        warehouse_id: params.warehouse_id || undefined,
        from: params.from || undefined,
        to: params.to || undefined,
        page: params.page || undefined,
        page_size: params.page_size || undefined,
        sort_by: params.sort_by || undefined,
        sort_dir: params.sort_dir || undefined,
      },
    })
  },
  getLocationLocks: (params?: {
    warehouse_id?: string
    location_code?: string
    page?: number
    page_size?: number
  }): CancelablePromise<InventoryCountLocationLockListResponseDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/inventory-count/locks",
      query: {
        warehouse_id: params?.warehouse_id || undefined,
        location_code: params?.location_code || undefined,
        page: params?.page || undefined,
        page_size: params?.page_size || undefined,
      },
    })
  },
  getActiveWarehouses:
    (): CancelablePromise<InventoryCountActiveWarehousesDTO> => {
      return __request(OpenAPI, {
        method: "GET",
        url: "/v1/inventory-count/active-warehouses",
      })
    },
  getBlockingDraftDocuments: (params: {
    warehouse_id: string
    limit?: number
  }): CancelablePromise<InventoryCountBlockingDraftDocumentsDTO> => {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/inventory-count/blocking-documents",
      query: {
        warehouse_id: params.warehouse_id,
        limit: params.limit || undefined,
      },
    })
  },
}
